import { useCallback } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { api } from "@/lib/api";
import type {
    ProviderContext,
    ProviderErrorBody,
} from "@/pages/Providers/types";

export const useProviderContext = (): ProviderContext => {
    const { t, i18n } = useTranslation("global");
    const auth = useAuth() as {
        token: string | null;
        user: { id: number } | null;
    };
    const { showToast } = useToast();
    const location = useLocation();
    const navigate = useNavigate();
    return {
        t,
        language: i18n.language,
        token: String(auth.token ?? ""),
        user: {
            id: Number(auth.user?.id),
            company: localStorage.getItem("vigepra_active_company") ?? "main",
        },
        router: { pathname: location.pathname, navigate },
        sendMessage: showToast as (message: string, severity?: string) => void,
    };
};

export class ProviderApiError extends Error {
    constructor(
        public readonly body: ProviderErrorBody,
        public readonly status: number
    ) {
        super(body.message ?? "providers.errors.request");
    }
}

export const useProviderApi = () => {
    const request = useCallback(
        async <T>(
            path: string,
            data?: object,
            signal?: AbortSignal,
            method?: "post" | "put"
        ): Promise<T> => {
            try {
                const response = await api.request<{
                    result: number;
                    data: T;
                    message?: string;
                }>({
                    url: `/providers${path}`,
                    method: method ?? (data ? "post" : "get"),
                    data,
                    signal,
                });
                if (!response.data.result) {
                    throw new ProviderApiError(
                        { message: response.data.message },
                        response.status
                    );
                }
                return response.data.data;
            } catch (error: unknown) {
                const cause =
                    error instanceof Error && error.cause ? error.cause : error;
                if (
                    axios.isAxiosError<ProviderErrorBody>(cause) &&
                    !axios.isCancel(cause)
                ) {
                    throw new ProviderApiError(
                        cause.response?.data ?? {},
                        cause.response?.status ?? 0
                    );
                }
                throw error;
            }
        },
        []
    );
    return { request };
};
